import Plotly from "plotly.js-dist-min";

const HIST_COLOR = "rgb(66, 133, 244)";
const HIST_LINE_COLOR = "rgb(0, 0, 0)";
const HIST_LINE_WIDTH = 1;
export const QA_QC_DIST_PLOT_HEIGHT = 360;
const PLOT_BG = "#e8edf5";
const GRID_COLOR = "rgba(0, 0, 0, 0.12)";

const BAR_PALETTE = ["#22c55e", "#f97316", "#ef4444"];

function histogramXBins(sortedData, binCount) {
  const n = Math.max(2, Math.min(100, Number(binCount) || 10));
  const minVal = Math.min(...sortedData);
  const maxVal = Math.max(...sortedData);
  const range = maxVal - minVal;
  if (range <= 0) {
    const pad = Math.max(Math.abs(minVal) * 1e-6, 1e-6);
    return {
      start: minVal - pad,
      end: maxVal + pad,
      size: (2 * pad) / n,
    };
  }
  const adjustedMinVal = Math.max(0, minVal);
  const adjustedRange = maxVal - adjustedMinVal;
  const padding = adjustedRange * 0.001;
  const binSize = (adjustedRange + 2 * padding) / n;
  return {
    start: Math.max(0, adjustedMinVal - padding),
    end: maxVal + padding,
    size: binSize,
  };
}

function barColorsForCategories(categories) {
  let idx = 0;
  return categories.map((cat) => {
    const s = String(cat || "");
    if (/N\/A|not reported|failed/i.test(s)) return "#9ca3af";
    const c = BAR_PALETTE[Math.min(idx, BAR_PALETTE.length - 1)];
    idx += 1;
    return c;
  });
}

function baseHistogramLayout(xTitle) {
  return {
    autosize: true,
    height: QA_QC_DIST_PLOT_HEIGHT,
    margin: { t: 24, r: 20, b: 72, l: 72 },
    showlegend: false,
    paper_bgcolor: "#ffffff",
    plot_bgcolor: PLOT_BG,
    bargap: 0.08,
    xaxis: {
      title: { text: xTitle, standoff: 12 },
      tickangle: -45,
      tickfont: { size: 11, color: "#374151" },
      showgrid: false,
      zeroline: false,
      showline: true,
      linecolor: "#9ca3af",
      linewidth: 1,
      mirror: false,
    },
    yaxis: {
      title: { text: "No. of Samples", standoff: 12 },
      tickfont: { size: 11, color: "#374151" },
      rangemode: "tozero",
      showgrid: true,
      gridcolor: GRID_COLOR,
      gridwidth: 1,
      zeroline: true,
      zerolinecolor: "#9ca3af",
      showline: true,
      linecolor: "#9ca3af",
      linewidth: 1,
      mirror: false,
    },
  };
}

function baseCategoricalLayout(xTitle) {
  return {
    autosize: true,
    height: QA_QC_DIST_PLOT_HEIGHT,
    margin: { t: 24, r: 20, b: 88, l: 72 },
    showlegend: false,
    paper_bgcolor: "#ffffff",
    plot_bgcolor: "#ffffff",
    bargap: 0.2,
    xaxis: {
      title: { text: xTitle, standoff: 8 },
      tickangle: -35,
      tickfont: { size: 10, color: "#374151" },
      type: "category",
      showgrid: false,
      showline: true,
      linecolor: "#9ca3af",
    },
    yaxis: {
      title: { text: "No. of Samples", standoff: 12 },
      tickfont: { size: 11, color: "#374151" },
      rangemode: "tozero",
      showgrid: true,
      gridcolor: GRID_COLOR,
      zeroline: true,
      zerolinecolor: "#9ca3af",
      showline: true,
      linecolor: "#9ca3af",
    },
  };
}

export function drawQaqcDistributionPlotly(el, viewData, numBins) {
  if (!el || !viewData) return Promise.resolve();

  const kind = viewData.plotKind;
  const xTitle = viewData.xAxisTitle || "";
  const config = { responsive: true, displayModeBar: false };

  if (kind === "histogram") {
    const raw = viewData.rawValues || [];
    if (raw.length === 0) {
      el.innerHTML = "";
      return Promise.resolve();
    }
    const sortedData = [...raw].map((v) => (typeof v === "string" ? parseFloat(v) : v)).sort((a, b) => a - b);
    const xbins = histogramXBins(sortedData, numBins);
    const trace = {
      x: sortedData,
      type: "histogram",
      name: xTitle || "Histogram",
      autobinx: false,
      nbinsx: Math.max(2, Math.min(100, Number(numBins) || 10)),
      marker: {
        color: HIST_COLOR,
        line: { color: HIST_LINE_COLOR, width: HIST_LINE_WIDTH },
      },
      hovertemplate: "Range: %{x}<br>No. of Samples: %{y}<extra></extra>",
    };
    if (xbins) trace.xbins = xbins;
    const layout = baseHistogramLayout(xTitle);
    return Plotly.react(el, [trace], layout, config);
  }

  if (kind === "categoricalBar") {
    const categories = viewData.categories || [];
    const counts = viewData.categoryCounts || [];
    if (categories.length === 0) {
      el.innerHTML = "";
      return Promise.resolve();
    }
    const colors = barColorsForCategories(categories);
    const trace = {
      x: categories,
      y: counts,
      type: "bar",
      marker: {
        color: colors,
        line: { color: "#1f2937", width: 1 },
      },
      hovertemplate: "%{x}<br>No. of Samples: %{y}<extra></extra>",
    };
    const layout = baseCategoricalLayout(xTitle);
    return Plotly.react(el, [trace], layout, config);
  }

  el.innerHTML = "";
  return Promise.resolve();
}
